/**
 * Global Reusable Button Component
 *
 * Usage:
 *   import { Button } from "@vionixasia/shared-components";
 *
 * Props:
 *   - variant: "primary" | "secondary" | "danger" | "ghost" | "outline" | "muted"
 *   - fullWidth: boolean
 *   - disabled: boolean
 *   - onClick: function
 *   - type: "button" | "submit" | "reset"
 *   - className: string (for additional custom classes)
 *   - children: ReactNode
 *
 * Dimensi selalu sama di semua project.
 * Perbedaan warna diatur via `variant`:
 *   - primary  → warna utama project (dikustomisasi via prop `color`)
 *   - secondary → abu-abu netral
 *   - danger   → merah (untuk aksi destruktif)
 *   - ghost    → transparan + border
 *   - outline   → transparan + border warna utama project
 *
 * Contoh custom warna project di luar variant:
 *   <Button color="cyan">Simpan</Button>   → livetiming
 *   <Button color="blue">Simpan</Button>   → livetrack
 *   <Button color="teal">Simpan</Button>   → main-frontend
 *   <Button color="red">Simpan</Button>    → travel (cultra)
 */

import React from "react";

const colorMap = {
  cyan: {
    solid: "bg-cyan-600 hover:bg-cyan-700 text-white",
    subtle: "bg-cyan-50 text-cyan-700 hover:bg-cyan-100",
    outline: "border border-cyan-600 text-cyan-600 hover:bg-cyan-50",
    ghost: "bg-transparent text-cyan-600 hover:bg-cyan-50",
  },
  blue: {
    solid: "bg-blue-600 hover:bg-blue-700 text-white",
    subtle: "bg-blue-50 text-blue-700 hover:bg-blue-100",
    outline: "border border-blue-600 text-blue-600 hover:bg-blue-50",
    ghost: "bg-transparent text-blue-600 hover:bg-blue-50",
  },
  indigo: {
    solid: "bg-indigo-600 hover:bg-indigo-700 text-white",
    subtle: "bg-indigo-50 text-indigo-700 hover:bg-indigo-100",
    outline: "border border-indigo-600 text-indigo-600 hover:bg-indigo-50",
    ghost: "bg-transparent text-indigo-600 hover:bg-indigo-50",
  },
  teal: {
    solid: "bg-teal-500 hover:bg-teal-600 text-white",
    subtle: "bg-teal-50 text-teal-700 hover:bg-teal-100",
    outline: "border border-teal-500 text-teal-500 hover:bg-teal-50",
    ghost: "bg-transparent text-teal-500 hover:bg-teal-50",
  },
  red: {
    solid: "bg-red-600 hover:bg-red-700 text-white",
    subtle: "bg-red-50 text-red-700 hover:bg-red-100",
    outline: "border border-red-600 text-red-600 hover:bg-red-50",
    ghost: "bg-transparent text-red-600 hover:bg-red-50",
  },
  slate: {
    solid: "bg-slate-900 hover:bg-slate-800 text-white",
    subtle: "bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900",
    outline: "border border-slate-900 text-slate-900 hover:bg-slate-50",
    ghost: "bg-transparent text-slate-600 hover:bg-slate-100",
  },
  gray: {
    solid: "bg-gray-200 hover:bg-gray-300 text-gray-800",
    subtle: "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900",
    outline: "border border-gray-300 text-gray-600 hover:bg-gray-50",
    ghost: "bg-transparent text-gray-600 hover:bg-gray-100",
  },
};

export const Button = ({
  children,
  onClick,
  disabled = false,
  variant = "primary",
  color = "gray",        // "cyan" | "blue" | "indigo" | "teal" | "red" | "slate" | "gray"
  fullWidth = false,
  type = "button",
  className = "",
  ...props
}) => {
  // Dimensi tetap — tidak ada size variant
  const base =
    "inline-flex items-center justify-center whitespace-nowrap gap-2 px-4 py-2.5 text-sm font-bold rounded-xl transition-colors duration-100 focus:outline-none !focus:ring-0 !focus:ring-offset-0";

  const widthClass = fullWidth ? "w-full" : "";

  const disabledClass = "opacity-50 cursor-not-allowed pointer-events-none";

  let colorClasses = "";

  switch (variant) {
    case "primary":
      colorClasses = colorMap[color]?.solid ?? colorMap.gray.solid;
      break;
    case "secondary":
      colorClasses = colorMap[color]?.subtle ?? colorMap.gray.subtle;
      break;
    case "ghost":
      colorClasses = colorMap[color]?.ghost ?? colorMap.gray.ghost;
      break;
    case "outline":
      colorClasses = colorMap[color]?.outline ?? colorMap.gray.outline;
      break;
    case "danger":
      colorClasses = "bg-red-600 hover:bg-red-700 text-white";
      break;
    case "muted":
      colorClasses = "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50";
      break;
    default:
      colorClasses = colorMap.gray.solid;
  }

  const Component = props.as || "button";
  
  return (
    <Component
      type={Component === "button" ? type : undefined}
      onClick={onClick}
      disabled={disabled}
      className={[
        base,
        colorClasses,
        widthClass,
        disabled ? disabledClass : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      {...props}
    >
      {children}
    </Component>
  );
};

export default Button;
